/* eslint-disable no-param-reassign */
import React from "react";
import { ColumnProps, DataTableCallbackRefs, DataTableGridOptions, IventisRowNode, ListItemCellEditorComponent, TIME_RANGE_FROM, TIME_RANGE_TO } from "@iventis/data-table";
import { generateListColumn, convertDataFieldsToColumns, removeFieldSuffixes, generateTextColumn } from "@iventis/data-table/helpers/column-generators";
import { DataField } from "@iventis/domain-model/model/dataField";
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { DomainLayer } from "@iventis/map-engine/src/state/map.state.types";
import { convertAreaMeasurement, convertLengthMeasurement, orderUniqueDataFields, removeRepeatedItemsFromArray, timeRangeFrom, timeRange, timeRangeTo } from "@iventis/utilities";
import { SimpleSortAction } from "@iventis/domain-model/model/simpleSortAction";
import { IventisSort } from "@iventis/domain-model/model/iventisSort";
import { IventisFilterOperator } from "@iventis/domain-model/model/iventisFilterOperator";
import { ExportFormat } from "@iventis/domain-model/model/exportFormat";
import { MapObjectsExport } from "@iventis/domain-model/model/mapObjectsExport";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import { MappingEngine } from "@iventis/map-engine";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { AreaModelRotation } from "@iventis/domain-model/model/areaModelRotation";
import { FilterFormat } from "@iventis/types";
import { mappingApi } from "@iventis/api/src/api";
import { v4 as uuid } from "uuid";
import { translate } from "@iventis/translations/translation";
import { Content } from "@iventis/translations";
import {
    getMapObjectAreaSystemDataField,
    getMapObjectCoordinatesSystemDataField,
    getMapObjectIconRotationSystemDataField,
    getMapObjectLengthSystemDataField,
    getMapObjectQuantityOfModelsOnLineSystemDataField,
    getMapObjectRotationZOfModelsInAreaSystemDataField,
    getModelAltitudeSystemDataField,
} from "@iventis/datafields";
import { getStaticStyleValue } from "@iventis/layer-style-helpers";
import { StyleType } from "@iventis/domain-model/model/styleType";
import { getMapObjectLevelName } from "../sidebar-library/sidebar-node.helpers";
import { orderUniqueDataFieldsAcrossLayers } from "../sidebar.helpers";
import { dataTableFilterToIventisFilter } from "../services/data-table-source";
import { MapDataTableEvent, mapDataTableEventStream } from "./sidebar-data-table-event-stream";
import { MapObjectRowNodeData } from "./sidebar-data-table.types";
import { PlansDataFieldListItemCellEditor } from "./sidebar-data-table-components";
import { rootStore } from "../state/root.store";
import { assetService } from "../services/asset-service";

export const LAYER_ID_FIELD_NAME = "layerId";

const LEVEL_FIELD_NAME = "level";

const LAYER_GROUP_BREADCRUMB_FIELD_NAME = "layerGroupBreadcrumb";

const getImageDataFieldName = async (imageId: string) => {
    if (imageId == null) {
        return "";
    }
    const asset = await assetService.getAsset(imageId);
    return asset?.name;
};

export const createColumns = async (
    layers: DomainLayer[],
    filteredLayers: DomainLayer[],
    setDataFields: (datafield: DataField[]) => void,
    projectDataFields: DataField[],
    unitOfMeasurement: UnitOfMeasurement,
    listItemGetter: (dataFieldId: string) => Promise<DataFieldListItem[]>,
    onRepeatedTimeRangeClick: (dataFieldId: string, rowData: MapObjectRowNodeData) => void,
    mappingEngine: MappingEngine
) => {
    let dataFields: DataField[] = [];

    const areaDataField = getMapObjectAreaSystemDataField(projectDataFields);
    const lengthDataField = getMapObjectLengthSystemDataField(projectDataFields);
    const coordsDataField = getMapObjectCoordinatesSystemDataField(projectDataFields);
    const quantityDataField = getMapObjectQuantityOfModelsOnLineSystemDataField(projectDataFields);
    const rotationZDataField = getMapObjectRotationZOfModelsInAreaSystemDataField(projectDataFields);
    const iconRotationDataField = getMapObjectIconRotationSystemDataField(projectDataFields);
    const modelAltitudeDataField = getModelAltitudeSystemDataField(projectDataFields);

    // Loop through the layers on the map and extract layerListValues and available datafields
    filteredLayers.forEach((layer) => {
        dataFields = [...dataFields, ...(layer.dataFields || [])];
    });

    setDataFields(dataFields);

    const isColumnDataField = (columnId: string) => dataFields.some((df) => df.id === columnId);

    // Ensure that the map object name datafield is the first datafield column in the table
    const orderedDataFields = filteredLayers.length === 1 ? orderUniqueDataFields(dataFields) : orderUniqueDataFieldsAcrossLayers(filteredLayers);

    const dataFieldListItemRequests = orderedDataFields.reduce(
        // Just resolve to empty array for non list item data fields
        (queries, dataField) => [...queries, [DataFieldType.List, DataFieldType.MultiSelect].includes(dataField.type) ? listItemGetter(dataField.id) : Promise.resolve([])],
        [] as Promise<DataFieldListItem[]>[]
    );

    const listItems = dataFieldListItemRequests.length > 0 ? await Promise.all(dataFieldListItemRequests) : [];

    // Set initial columns
    const columns: ColumnProps[] = removeRepeatedItemsFromArray((c) => c.agGridColumnProps.field, [
        generateListColumn(
            { componentParams: { listItems: layers, initialFilter: filteredLayers?.length > 0 ? filteredLayers : undefined } },
            { field: LAYER_ID_FIELD_NAME, headerName: "Layer" },
            { editable: false },
            "IventisFilterGuidArray"
        ),
        generateTextColumn({ field: LAYER_GROUP_BREADCRUMB_FIELD_NAME, headerName: "Layer group" }, { editable: false, sortable: false, filter: false }),
        generateTextColumn({ field: LEVEL_FIELD_NAME, headerName: "Level" }, { editable: false, sortable: false, filter: false }),
        ...convertDataFieldsToColumns(
            orderedDataFields.map((dataField, index) => ({ ...dataField, listValues: listItems[index] })),
            {
                // Value getters and setters are needed because data field values are not presented flatly in the row data. They are nested.
                valueGetter: (dataFieldId, dataFieldType) => ({ node }: { node: IventisRowNode<MapObjectRowNodeData> }) => {
                    const value = node.data?.dataFieldValues?.[dataFieldId];
                    // Assuming that df is a repeated time range
                    if (dataFieldType === DataFieldType.RepeatedTimeRanges) {
                        if (value !== undefined && Array.isArray(value) && value.length > 0) {
                            return `${value.length} ${translate(Content.map8.ranges)}`;
                        }
                        return `0 ${translate(Content.map8.ranges)}`;
                    }
                    // If the data field value is an object we know this is a grouped row, so it's either the only value or undefined
                    if (typeof value === "object" && value != null && value.listItemAggregations != null) {
                        const listItemIds = Object.keys(value.listItemAggregations);
                        if (listItemIds.length === 1) {
                            return listItemIds[0];
                        }
                        return undefined;
                    }

                    const layer = layers.find((l) => l.id === node.data.layerId);

                    // Handle special cases for system data fields for layer rows
                    if (!node.group) {
                        switch (dataFieldId) {
                            case rotationZDataField.id:
                                if (getStaticStyleValue(layer.areaModelStyle?.rotationType) === AreaModelRotation.Random) {
                                    return translate(Content.map10.areaModel.rotation.random);
                                }
                                break;
                            case iconRotationDataField.id:
                                if (layer.styleType === StyleType.Icon) {
                                    return value ?? 0;
                                }
                                break;
                            case modelAltitudeDataField.id:
                                if (layer.styleType === StyleType.Model) {
                                    return value ?? 0;
                                }
                                break;
                            default:
                                break;
                        }
                    }
                    return value;
                },
                valueFormatter: (dataFieldId) => ({ value }) => {
                    if (value == null) {
                        return undefined;
                    }
                    if (dataFieldId === areaDataField.id) {
                        return convertAreaMeasurement(value, unitOfMeasurement);
                    }
                    if (dataFieldId === lengthDataField.id) {
                        return convertLengthMeasurement(value, unitOfMeasurement);
                    }
                    return value;
                },
                valueSetter: ({ newValue, column, data }) => {
                    const field = column.getColId();
                    const fieldWithNoSuffixes = removeFieldSuffixes(field);
                    if (isColumnDataField(fieldWithNoSuffixes)) {
                        data.dataFieldValues[fieldWithNoSuffixes] = field.includes(TIME_RANGE_FROM)
                            ? timeRange(newValue, timeRangeTo(data.dataFieldValues[fieldWithNoSuffixes]))
                            : field.includes(TIME_RANGE_TO)
                            ? timeRange(timeRangeFrom(data.dataFieldValues[fieldWithNoSuffixes]), newValue)
                            : newValue;
                    } else {
                        data[field] = newValue;
                    }
                    return true;
                },
                cellEditable: (row: MapObjectRowNodeData, dataFieldId) => {
                    const layer = layers.find((l) => l.id === row.layerId);
                    const what3wordsDataField = layer.dataFields.find((df) => df.type === DataFieldType.What3Words);
                    const imageAttributes = layer.dataFields.filter((df) => df.type === DataFieldType.Image).map((df) => df.id);
                    return (
                        mappingEngine === MappingEngine.Mapbox &&
                        row.canEdit &&
                        layer.dataFields.some((df) => df.id === dataFieldId) &&
                        dataFieldId !== areaDataField?.id &&
                        dataFieldId !== lengthDataField?.id &&
                        dataFieldId !== coordsDataField?.id &&
                        dataFieldId !== what3wordsDataField?.id &&
                        dataFieldId !== quantityDataField?.id &&
                        !(dataFieldId === rotationZDataField?.id && layer.areaModelStyle.rotationType.staticValue.staticValue === AreaModelRotation.Random) &&
                        !imageAttributes.includes(dataFieldId)
                    );
                },
                listItemCellEditorComponent: PlansDataFieldListItemCellEditor as typeof ListItemCellEditorComponent,
                asyncTextGetter: getImageDataFieldName,
                onHyperlinkClick: (columnId, rowData: MapObjectRowNodeData): void => {
                    const dataField = dataFields.find((df) => df.id === columnId);
                    if (dataField.type === DataFieldType.RepeatedTimeRanges) {
                        onRepeatedTimeRangeClick(columnId, rowData);
                    }
                },
            }
        ),
    ]);
    return columns;
};

export const onFilterChange = async (
    layers: DomainLayer[],
    setDataFields: (datafield: DataField[]) => void,
    projectDataFields: DataField[],
    unitOfMeasurement: UnitOfMeasurement,
    listItemGetter: (dataFieldId: string) => Promise<DataFieldListItem[]>,
    onRepeatedTimeRangeClick: (dataFieldId: string, rowData: MapObjectRowNodeData) => void,
    mappingEngine: MappingEngine,
    layersToShow?: { id: string }[]
) => {
    const filteredLayers = layersToShow === undefined ? layers : layers.filter((l) => layersToShow.some((layer) => layer.id === l.id));
    const columns = await createColumns(layers, filteredLayers, setDataFields, projectDataFields, unitOfMeasurement, listItemGetter, onRepeatedTimeRangeClick, mappingEngine);
    return columns;
};

/** Returns a subscription to the updates from the map (e.g. selections and deletions) and makes the appropriate updates to the data table  */
export const subscriptionToMapObjectChanges = (gridRef: React.MutableRefObject<DataTableCallbackRefs<MapObjectRowNodeData>>, sitemaps: Sitemap[]) =>
    mapDataTableEventStream.subject.subscribe((event) => {
        const api = gridRef.current?.getApi();
        if (api) {
            switch (event.type) {
                case MapDataTableEvent.MapObjectsCreatedRemotely:
                case MapDataTableEvent.MapObjectsUpdatedRemotely: {
                    // Because we can't do transactions on an infinite row model, we must instead refresh the infinite cache and the map objects should then appear
                    gridRef.current?.refreshIndex();
                    break;
                }
                case MapDataTableEvent.SitemapConfigsUpdated: {
                    const rows = api.getRenderedNodes();
                    rows.forEach((row) => {
                        const newLevelName = getMapObjectLevelName(sitemaps, row?.data.geoJsonFeature.properties.level, row?.data.sitemapId, event.payload);
                        row?.setDataValue("level", newLevelName, "external");
                    });
                    break;
                }
                case MapDataTableEvent.MapObjectsDeletedRemotely: {
                    event.payload.forEach((id) => {
                        const row = api.getRowNode(id);
                        if (row?.isSelected()) {
                            row?.setSelected(false);
                            api.clearRangeSelection();
                            api.clearFocusedCell();
                        }
                    });
                    // Because we can't do transactions on an infinite row model, we must instead refresh the infinite cache and the deleted map objects should then disappear
                    gridRef.current?.refreshIndex();
                    break;
                }
                case MapDataTableEvent.MapObjectsSelected: {
                    gridRef.current?.selectFromOutside(event.payload.map(({ objectId }) => objectId));
                    break;
                }
                default:
                    // @ts-expect-error If we add a new event and forget to add a case for it, typescript will give us an error at compile time
                    throw new Error(event.type);
            }
        }
    });

/** Called each time the data table requests data from the backend */
export const onPaginationChanged = (params: Parameters<DataTableGridOptions<MapObjectRowNodeData>["onPaginationChanged"]>[0]) => {
    const currentTableSelection = params.api.getSelectedNodes();
    const currentSliceSelection = rootStore.getState().mapReducer.mapModule.mapObjectsSelected.value;
    currentTableSelection.forEach((node) => {
        // If the table selection isn't in the slice's selection, deselect it
        if (!currentSliceSelection.some((object) => object.objectId === node.id)) {
            node.setSelected(false);
        }
    });
    currentSliceSelection.forEach((selection) => {
        const row = params.api.getRowNode(selection.objectId);
        // If the object selected in the slice is is the table and not currently selected, then select it
        if (row && !row.isSelected()) {
            row.setSelected(true);
        }
    });
};

export const exportDataTable = (
    gridRef: DataTableCallbackRefs<MapObjectRowNodeData>,
    mapId: string,
    layerIds: string[],
    columnIdsToNameMapping: { [key: string]: string },
    unitOfMeasurement: UnitOfMeasurement,
    columnsToExport: string[],
    mapObjectIdFilter?: string[]
): MapObjectsExport => {
    // Get datatable contexts
    const dtFilters = gridRef.getApi().getFilterModel();
    const dtColumns = gridRef.getColumnApi().getColumnState();

    // Get sorts from each column
    const toSort: IventisSort<SimpleSortAction>[] = dtColumns.reduce((arr, c) => {
        let fieldName: string = c.colId;

        switch (fieldName) {
            case "layerId":
                fieldName = "LayerId";
                break;
            case "name":
                fieldName = "Name";
                break;
            default:
                break;
        }

        if (c.sort) {
            return [
                ...arr,
                {
                    fieldName,
                    // Capitalise first letter to convert to sort action
                    sortAction: SimpleSortAction[c.sort.charAt(0).toUpperCase() + c.sort.slice(1)],
                },
            ];
        }
        return arr;
    }, [] as IventisSort<SimpleSortAction>[]);

    const mapObjectFilter: FilterFormat[] =
        mapObjectIdFilter == null
            ? []
            : [
                  {
                      $type: "IventisFilterGuidArray",
                      fieldName: "mapObjectId",
                      operator: IventisFilterOperator.In,
                      value: mapObjectIdFilter,
                  },
              ];

    // Convert filters, and if no layer ids are filtered on, we need to pass up all the layer ids in the map
    const filters = !dtFilters?.layerId
        ? [
              ...dataTableFilterToIventisFilter(dtFilters),
              {
                  // We need to add a type here  so that the filters can be correctly serialized on the back end
                  // This only needs to be done where the request filter is in the payload
                  // This also ALWAYS must be first in the object
                  $type: "IventisFilterGuidArray",
                  fieldName: "layerId",
                  operator: IventisFilterOperator.In,
                  value: layerIds,
              },
              ...mapObjectFilter,
          ]
        : [...dataTableFilterToIventisFilter(dtFilters), ...mapObjectFilter];

    const req: MapObjectsExport = {
        mapId,
        layerIds: undefined,
        export: undefined,
        exportFormat: ExportFormat.Excel,
        filters,
        sorts: toSort,
        unitOfMeasurement,
        includeDataFields: columnsToExport.reduce((dataFields, columnName) => {
            if (columnName === "layerId" || columnName === "level" || columnName === "layerGroupBreadcrumb") {
                return dataFields;
            }
            if (dataFields.includes(removeFieldSuffixes(columnName))) {
                return dataFields;
            }
            return [...dataFields, removeFieldSuffixes(columnName)];
        }, []),
        includeLevelColumn: columnsToExport.includes("level"),
        includeLayerColumn: columnsToExport.includes("layerId"),
        includeGroupColumn: columnsToExport.includes("layerGroupBreadcrumb"),
        notificationId: uuid(),
        cadOptions: null,
    };

    mappingApi.post(`maps/${mapId}/map_objects/exports`, req, {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.export_error.title, { entity: translate(Content.map9.map_objects) }),
            message: translate(Content.errors.custom_network_error.export_error.message, { entity: translate(Content.map9.map_objects) }),
        },
    });

    return req;
};
